import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import { PlatoChip } from "@ats/src/components/shared/PlatoMark";

export type PlatoGenerationStatus =
  | "textract_processing"
  | "extracting"
  | "summarizing"
  | "awaiting_job_criteria"
  | "scoring"
  | "integrating";

const STEPS = [
  "Processing the resume",
  "Analyzing the candidate",
  "Scoring against the role",
  "Finalizing the review",
];

export const STATUS_TO_STEP: Record<PlatoGenerationStatus, number> = {
  textract_processing: 0,
  extracting: 1,
  summarizing: 1,
  awaiting_job_criteria: 2,
  scoring: 2,
  integrating: 3,
};

export interface PlatoLoadingStateProps {
  status?: string;
}

type StepState = "done" | "active" | "todo";

const PlatoLoadingState: React.FunctionComponent<PlatoLoadingStateProps> = ({ status }) => {
  const target = status && status in STATUS_TO_STEP ? STATUS_TO_STEP[status as PlatoGenerationStatus] : null;
  const [active, setActive] = React.useState<number>(target != null ? target : 0);
  const [showKeepWorking, setShowKeepWorking] = React.useState(false);
  React.useEffect(() => {
    if (target == null) return;
    setActive((prev) => Math.max(prev, target));
  }, [target]);
  React.useEffect(() => {
    const timer = setTimeout(() => setShowKeepWorking(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Styled.Container>
      <Styled.IconWrapper>
        <PlatoChip size={40} />
      </Styled.IconWrapper>
      <h2>Plato is reviewing this candidate</h2>
      <p>Reviewing role fit, relevant experience, skills, and gaps.</p>

      <Styled.Steps>
        {STEPS.map((label, i) => {
          const state: StepState = i < active ? "done" : i === active ? "active" : "todo";
          return (
            <Styled.Step key={label} data-state={state}>
              <Styled.Mark data-state={state}>
                {state === "done" ? <Icon name="check" /> : state === "active" ? <Styled.Spinner /> : <Styled.Circle />}
              </Styled.Mark>
              <span>{label}</span>
            </Styled.Step>
          );
        })}
      </Styled.Steps>

      {showKeepWorking && (
        <Styled.KeepWorking>You can keep working — Plato will finish in the background.</Styled.KeepWorking>
      )}
    </Styled.Container>
  );
};

export default PlatoLoadingState;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoLoadingState;
    ${[t.px(6), t.py(10), t.rounded.md]}
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
    background-color: ${t.dark ? "rgba(255, 255, 255, 0.07)" : "transparent"};
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    h2 {
      ${[t.text.h5, t.mt(1), t.mb(1)]}
    }
    p {
      ${t.text.sm}
      max-width: 24rem;
      line-height: 1.45;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.IconWrapper = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoLoadingState_IconWrapper;
    ${t.mb(2)}
    display: inline-flex;
  `;
});

Styled.Steps = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoLoadingState_Steps;
    ${[t.mt(3), t.mb(1)]}
    display: inline-flex;
    flex-direction: column;
    gap: ${t.spacing[3]};
    text-align: left;
  `;
});

Styled.Step = styled.div((props: any) => {
  const t: any = props.theme;
  const state = props["data-state"];
  return css`
    label: PlatoLoadingState_Step;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
    ${t.text.sm}
    line-height: 1;
    color: ${state === "active"
      ? t.dark ? t.color.gray[100] : t.color.black
      : state === "done"
      ? t.dark ? t.color.gray[400] : t.color.gray[600]
      : t.color.gray[400]};
    ${state === "active" && t.text.medium}
  `;
});

Styled.Mark = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoLoadingState_Mark;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${props["data-state"] === "done" ? t.color.gray[400] : "inherit"};

    svg {
      width: 16px;
      height: 16px;
    }
  `;
});

Styled.Circle = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid ${t.color.gray[300]};
  `;
});

Styled.Spinner = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    width: 15px;
    height: 15px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid ${t.color.gray[300]};
    border-top-color: ${t.dark ? t.color.gray[100] : t.color.black};
    animation: plato-spin 0.8s linear infinite;
    @keyframes plato-spin {
      to {
        transform: rotate(360deg);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
});

Styled.KeepWorking = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoLoadingState_KeepWorking;
    ${[t.text.xs, t.mt(4)]}
    max-width: 20rem;
    line-height: 1.4;
    color: ${t.color.gray[500]};
  `;
});
